"use client";

import Pagination from "@/components/ui/Pagination";
import { useCategories, useExperiences } from "@/hooks/usePublicData";
import { getImageWithFallback } from "@/lib/default-image";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import Riyal from "@/components/ui/Riyal";

function Icon({ name, className = "", fill = false }: { name: string; className?: string; fill?: boolean }) {
  return <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>{name}</span>;
}

export default function DestinationsClient() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const t = (a: string, e: string) => (isAr ? a : e);

  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: categories = [] } = useCategories();
  const { data: res, isLoading } = useExperiences({
    page,
    limit: 12,
    categoryId: activeCat !== "all" ? activeCat : undefined,
  });
  const pagination = res?.pagination;

  // Reset to page 1 via handlers (not an effect — avoids cascading renders).
  const onCat = (id: string) => {
    setActiveCat(id);
    setPage(1);
  };
  const onSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const chips = useMemo(
    () => [{ id: "all", name: { ar: "الكل", en: "All" } }, ...categories],
    [categories],
  );

  const experiences = (res?.data ?? []).filter((e: any) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      e.title?.ar?.toLowerCase().includes(q) ||
      e.title?.en?.toLowerCase().includes(q) ||
      e.location?.address?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="najdi bg-background text-ink-900" dir={isAr ? "rtl" : "ltr"}>
      {/* ── Compact header (no image hero) ── */}
      <section className="container mx-auto px-6 pt-8 pb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-2">
          {t("استكشف التجارب", "Explore experiences")}
        </h1>
        <p className="text-ink-500 mb-6">
          {t("أنشطة فريدة في جميع أنحاء المملكة العربية السعودية.", "Unique activities across Saudi Arabia.")}
        </p>
        <div className="relative max-w-xl">
          <Icon name="search" className="absolute top-1/2 -translate-y-1/2 inset-s-4 text-ink-500 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="w-full ps-12 pe-4 py-3 rounded-full border border-clay-100 bg-surface text-ink-900 placeholder:text-ink-500/60 outline-none focus:border-clay-600 text-sm"
            placeholder={t("ابحث عن تجارب، مدن...", "Search experiences, cities...")}
          />
        </div>
      </section>

      {/* ── Category filter (sticky) ── */}
      <div className="sticky top-[72px] z-40 bg-surface/95 backdrop-blur-md border-b border-clay-100 py-3 shadow-sm">
        <div className="container mx-auto px-6 flex items-center gap-3 overflow-x-auto no-scrollbar">
          {chips.map((c: any) => {
            const id = c.id ?? c._id;
            const active = activeCat === id;
            return (
              <button
                key={id}
                onClick={() => onCat(id)}
                className={`shrink-0 px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
                  active ? "bg-clay-600 text-white" : "bg-clay-50 text-ink-900 hover:bg-clay-100"
                }`}
              >
                {isAr ? c.name?.ar : c.name?.en}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Grid ── */}
      <section className="container mx-auto px-6 py-12 min-h-[50vh]">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-4/5 rounded-[20px] bg-clay-100/40 mb-4" />
                <div className="h-4 w-2/3 bg-clay-100/40 rounded mb-2" />
                <div className="h-3 w-1/2 bg-clay-100/40 rounded" />
              </div>
            ))}
          </div>
        ) : experiences.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <Icon name="explore_off" className="text-ink-500 text-5xl" />
            <p className="text-ink-500 font-bold">{t("لا توجد تجارب مطابقة", "No experiences found")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((e: any) => {
              const title = isAr ? e.title.ar : e.title.en;
              const loc = e.city || e.location?.address || (e.categoryId ? (isAr ? e.categoryId.name.ar : e.categoryId.name.en) : "");
              const cat = e.categoryId ? (isAr ? e.categoryId.name.ar : e.categoryId.name.en) : null;
              const img = getImageWithFallback(e.mainImage, "destination");
              const meta = [
                e.maxPeople ? `${t("حتى", "up to")} ${e.maxPeople} ${t("أشخاص", "people")}` : null,
                e.dateFrom && e.dateTo ? `${e.dateFrom} → ${e.dateTo}` : null,
              ].filter(Boolean).join(" • ");
              return (
                <Link key={e._id || e.id} href={`/${locale}/destinations/${e._id || e.id}`} className="group cursor-pointer">
                  <div className="relative aspect-4/5 rounded-[20px] overflow-hidden mb-4 shadow-sm group-hover:shadow-xl transition-all duration-500">
                    <Image src={img} alt={title} fill unoptimized sizes="(max-width:640px)100vw,(max-width:1024px)50vw,25vw" className="object-cover group-hover:scale-105 transition-transform duration-500" />
                    {cat && (
                      <div className="absolute top-4 inset-s-4 bg-clay-600/90 text-white px-3 py-1 rounded-full text-xs font-bold">{cat}</div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="font-bold text-lg text-ink-900 truncate">{title}</h3>
                      {e.rating ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <Icon name="star" fill className="text-gold-500 text-[16px]" />
                          <span className="font-bold text-sm">{e.rating}</span>
                        </div>
                      ) : null}
                    </div>
                    {loc && <p className="text-ink-500 text-sm truncate">{loc}</p>}
                    {meta && <p className="text-ink-500/80 text-sm">{meta}</p>}
                    <div className="flex justify-between items-center pt-2">
                      <p className="font-bold text-clay-600 text-lg">
                        {e.price?.toLocaleString()} <Riyal />
                        <span className="font-normal text-sm text-ink-500"> / {t("للشخص", "person")}</span>
                      </p>
                      <span className="text-xs text-ink-500">{t("يقدمها منازل الدرعية", "by Diriyah Homes")}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <Pagination
          page={page}
          totalPages={pagination?.totalPages ?? 0}
          onChange={setPage}
          isAr={isAr}
        />
      </section>

      {/* ── Why choose us ── */}
      <section className="bg-clay-50 najdi-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-3">{t("لماذا تختار تجاربنا؟", "Why choose our experiences?")}</h2>
            <p className="text-ink-500 max-w-2xl mx-auto">{t("نضمن لك رحلة غنية بالأصالة عبر معايير اختيار دقيقة لكل تجربة.", "A rich, authentic journey through carefully curated experiences.")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "verified_user", ti: t("مضيفون موثّقون", "Verified hosts"), de: t("خبراء محليون تم التحقق منهم لتجربة أصيلة وآمنة.", "Verified local experts for a safe, authentic experience.") },
              { icon: "group", ti: t("مجموعات صغيرة", "Small groups"), de: t("نركّز على المجموعات الصغيرة لاهتمام شخصي أعمق.", "Small groups for deeper, personal attention.") },
              { icon: "event_busy", ti: t("إلغاء مجاني", "Free cancellation"), de: t("مرونة كاملة في الإلغاء حتى 24 ساعة قبل التجربة.", "Full flexibility up to 24h before the experience.") },
              { icon: "workspace_premium", ti: t("جودة مضمونة", "Guaranteed quality"), de: t("نراقب ملاحظات الضيوف لضمان أعلى المعايير.", "We monitor guest feedback for the highest standards.") },
            ].map((f) => (
              <div key={f.icon} className="bg-surface p-8 rounded-[20px] shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-16 h-16 bg-clay-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name={f.icon} className="text-clay-600 text-3xl" />
                </div>
                <h3 className="font-bold text-lg mb-3 text-ink-900">{f.ti}</h3>
                <p className="text-ink-500 text-sm leading-relaxed">{f.de}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-3">{t("كيف تعمل المنصة؟", "How it works")}</h2>
          <p className="text-ink-500">{t("خطوات بسيطة تفصلك عن مغامرتك القادمة.", "A few simple steps to your next adventure.")}</p>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 inset-x-0 h-px border-t-2 border-dotted border-clay-600/30 -translate-y-1/2 z-0" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative z-10">
            {[
              { n: "01", icon: "search_check", ti: t("اختر تجربتك", "Choose"), de: t("تصفّح مئات التجارب وفلتر حسب اهتماماتك.", "Browse and filter by your interests.") },
              { n: "02", icon: "calendar_today", ti: t("احجز موعدك", "Book"), de: t("اختر التاريخ المناسب واحجز فوراً.", "Pick a date and book instantly.") },
              { n: "03", icon: "person_pin_circle", ti: t("قابل مضيفك", "Meet"), de: t("التقِ مضيفك المحلي في نقطة التجمع.", "Meet your local host at the meeting point.") },
              { n: "04", icon: "reviews", ti: t("عِش التجربة", "Enjoy"), de: t("استمتع بكل لحظة وشارك تقييمك.", "Enjoy every moment and share your review.") },
            ].map((s) => (
              <div key={s.n} className="relative text-center group">
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-[100px] font-extrabold text-clay-600/10 select-none">{s.n}</div>
                <div className="w-16 h-16 bg-clay-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg relative">
                  <Icon name={s.icon} />
                </div>
                <h3 className="font-bold text-lg mb-2 text-ink-900">{s.ti}</h3>
                <p className="text-ink-500 text-sm px-4">{s.de}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured experiences (carousel) ── */}
      {experiences.length > 0 && (
        <section className="bg-clay-50 py-20 overflow-hidden">
          <div className="container mx-auto px-6">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-2">{t("تجارب الشهر المميزة", "Featured this month")}</h2>
            <p className="text-ink-500 mb-10">{t("مختاراتنا لأفضل التجارب هذا الموسم.", "Our picks for the season.")}</p>
            <div className="flex gap-6 overflow-x-auto pb-6 no-scrollbar scroll-smooth">
              {experiences.slice(0, 8).map((e: any) => {
                const title = isAr ? e.title.ar : e.title.en;
                const desc = (isAr ? e.description?.ar : e.description?.en) || "";
                const cat = e.categoryId ? (isAr ? e.categoryId.name.ar : e.categoryId.name.en) : "";
                const img = getImageWithFallback(e.mainImage, "destination");
                return (
                  <Link key={e._id || e.id} href={`/${locale}/destinations/${e._id || e.id}`} className="min-w-[300px] md:min-w-[380px] bg-surface rounded-[24px] overflow-hidden shadow-sm group">
                    <div className="h-56 relative">
                      <Image src={img} alt={title} fill unoptimized className="object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6">
                      {cat && <span className="text-clay-600 text-[12px] font-bold tracking-widest uppercase mb-2 block">{cat}</span>}
                      <h4 className="font-bold text-lg mb-2 text-ink-900 truncate">{title}</h4>
                      {desc && <p className="text-ink-500 text-sm mb-4 line-clamp-2">{desc}</p>}
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-clay-600">{t("من", "From")} {e.price?.toLocaleString()} <Riyal /></span>
                        <Icon name={isAr ? "arrow_back" : "arrow_forward"} className="text-clay-600" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
